import {RemoteConstantsModel} from "../models/remoteConstants.model";
import {AttributeManifest, AttributeManifestEntry} from "../models/attributeManifest.model";

// =============================================================================
// Image classification — the single source of truth for what an image key IS
// (section), what to CALL it (label — always derived, never authored), and
// who USES it (usedBy; empty = orphan).
//
// Two-level model: the SECTION comes from the key's identity (its namespace /
// owning entity), while usedBy accumulates every reference — so one image can
// live in "Resources" yet also serve as a tech action's icon, and both facts
// are visible. Replaces the old imageLabels resolver and the authored
// displayName field (now inert in data): since every upload is keyed by its
// first target, art always has a usage to derive a name from, and for
// code-referenced sprites the key IS the name.
// =============================================================================

export type ImageSectionId =
    | 'units'
    | 'buildings'
    | 'tiles'
    | 'resources'
    | 'metadata-icons'
    | 'ui'
    | 'unclassified';

export const SECTION_ORDER: ImageSectionId[] = [
    'units', 'buildings', 'tiles', 'resources', 'metadata-icons', 'ui', 'unclassified',
];

export const SECTION_TITLES: Record<ImageSectionId, string> = {
    'units': 'Units',
    'buildings': 'Buildings',
    'tiles': 'Tiles',
    'resources': 'Resources',
    'metadata-icons': 'Icons',
    'ui': 'UI',
    'unclassified': 'Unclassified',
};

export interface ImageUsage {
    kind: 'entity' | 'packSlot' | 'metadataIcon' | 'construction' | 'code';
    ref: string;     // entity id / pack owner id / metadata uid / the key itself for code
    label: string;   // human-readable description of the usage
}

export interface ClassifiedImage {
    key: string;
    section: ImageSectionId;
    label: string;          // virtual name, derived from identity/usage
    usedBy: ImageUsage[];   // empty = orphan (but see `unclassified` caveat)
    // Set when the image is a visible slot of an entity's asset pack. Lets the
    // Images tab group a pack's images under one representative (`main`).
    pack?: { owner: string; level: number; variation: number; main: boolean };
    // Set when the image is a building's construction frame (a flat ordered list
    // entry, NOT a pack slot). Lets the Images tab group it under the building
    // (alongside the pack) instead of leaving it loose.
    constructionOf?: { owner: string; index: number };
}

// Image keys the Client asks for by name are invisible to any data scan, so they
// come from the attribute manifest's ImageKeys ([ImageKey] fields in C#, plus keys
// typed into image targets on prefabs/scenes). This hand-kept list is only the
// fallback for a manifest exported before ImageKeys existed: without it every
// code key would read as an orphan and be offered for cleanup. Delete it once
// every environment's manifest carries ImageKeys.
const LEGACY_CODE_IMAGE_KEYS = new Set<string>([
    'gold', 'Mana', 'units',
    'Team Shield', 'Veteran Star', 'Gold Pile', 'SettleOrder',
    'conversion', 'defeated', 'heal',
    'fog', 'fog.puff', 'cloud.puff',
    'tile.cloud',
    'Egg',
]);

// key → usage label for every image key the Client names.
function codeImageKeys(manifest: AttributeManifest | undefined): Map<string, string> {
    const result = new Map<string, string>();
    if (!manifest?.ImageKeys) {
        LEGACY_CODE_IMAGE_KEYS.forEach(key => result.set(key, 'Client code — key is referenced from C#'));
        return result;
    }
    for (const entry of manifest.ImageKeys) {
        const where = entry.Sources.join(', ');
        result.set(entry.Id, entry.Description
            ? `Client: ${entry.Description} (${where})`
            : `Client: ${where}`);
    }
    return result;
}

// An image the Client will ask this branch for and not find.
export interface MissingImage {
    key: string;
    description: string;
    sources: string[];
}

// Every image the Client will ask for that this branch has no entry for, sorted
// by key. Three kinds:
//   - keys the Client names (manifest ImageKeys: [ImageKey] fields, prefab keys);
//   - each player order's action button (manifest Orders: ButtonImageKey), and its
//     strip icon (IconKey) when units have the order — the info-bar strip is
//     unit-only, so a building order's icon (Capture's, Repair's) is never drawn;
//   - the icon of each metadata entry whose attribute type the Client draws one
//     for ([AttributeIcon]: spells, tech actions, tech passives), resolved the way
//     RemoteConstants.GetEntityImageKey does — the image its imagePacks[uid] slot
//     points at, else the uid itself.
// In game each falls back to Master's image when Master has one, else shows the
// "?" placeholder; the editor only holds this branch, so it can't tell which.
// Empty for a manifest without ImageKeys: the legacy list is too stale to accuse with.
export function missingImages(
    state: RemoteConstantsModel,
    attributeManifest: AttributeManifest | undefined,
    getLocalizationValue: (id: string) => string,
): MissingImage[] {
    const images = state.images ?? {};
    const missing = new Map<string, MissingImage>();

    for (const entry of attributeManifest?.ImageKeys ?? []) {
        if (entry.Id in images) continue;
        missing.set(entry.Id, {key: entry.Id, description: entry.Description ?? '', sources: [...entry.Sources]});
    }

    for (const order of attributeManifest?.Orders ?? []) {
        const icon = order.AppliesTo?.includes('unit') ? order.IconKey : undefined;
        const roles: Array<[string | null | undefined, string]> = [[icon, 'icon'], [order.ButtonImageKey, 'button']];
        for (const [key, role] of roles) {
            if (!key || key in images) continue;
            const source = `${order.TypeName} (${role})`;
            const existing = missing.get(key);
            if (existing) existing.sources.push(source);
            else missing.set(key, {key, description: `Order ${role}`, sources: [source]});
        }
    }

    const iconOf = new Map<string, string>();
    const iconTypes: Array<AttributeManifestEntry[] | undefined> = [
        attributeManifest?.UnitAttributes, attributeManifest?.TechActions, attributeManifest?.TechPassives,
    ];
    for (const list of iconTypes)
        for (const entry of list ?? [])
            if (entry.Icon) entry.Ids.forEach(id => iconOf.set(id, entry.Icon!));

    const metadata = [
        ...(state.unitAttributes ?? []), ...(state.techActionAttributes ?? []), ...(state.techPassiveAttributes ?? []),
    ];
    for (const meta of metadata) {
        const icon = iconOf.get(meta.id);
        if (!icon || !meta.uid || meta.active === false) continue;
        const key = state.imagePacks?.[meta.uid]?.[0]?.[0] || meta.uid;
        if (key in images) continue;
        const source = `${icon}: ${getLocalizationValue(meta.uid)} (${meta.uid})`;
        const existing = missing.get(key);
        if (existing) existing.sources.push(source);
        else missing.set(key, {key, description: `${icon} icon`, sources: [source]});
    }

    return Array.from(missing.values()).sort((a, b) => a.key.localeCompare(b.key));
}

const SLOT_KEY_RE = /^(.+)_(\d+)_(\d+)$/;

export function classifyImageKeys(
    state: RemoteConstantsModel,
    getLocalizationValue: (id: string) => string,
    attributeManifest: AttributeManifest | undefined,
): Map<string, ClassifiedImage> {
    const codeKeys = codeImageKeys(attributeManifest);

    // --- lookup tables --------------------------------------------------
    const entitySection = new Map<string, ImageSectionId>();
    state.units?.forEach(e => entitySection.set(e.id, 'units'));
    state.buildings?.forEach(e => entitySection.set(e.id, 'buildings'));
    state.tiles?.forEach(e => entitySection.set(e.id, 'tiles'));
    state.resources?.forEach(e => entitySection.set(e.id, 'resources'));

    // uid → human collection name, for self-describing usage labels.
    const metadataCollectionOf = new Map<string, string>();
    const metaColls: Array<[typeof state.unitAttributes, string]> = [
        [state.unitAttributes, 'Unit attribute'],
        [state.buildingAttributes, 'Building attribute'],
        [state.techActionAttributes, 'Tech action'],
        [state.techPassiveAttributes, 'Tech passive'],
        [state.rewards, 'Reward'],
    ];
    for (const [coll, name] of metaColls)
        coll?.forEach(m => { if (m.uid) metadataCollectionOf.set(m.uid, name); });
    const metadataUids = new Set(Array.from(metadataCollectionOf.keys()));

    const KIND_SINGULAR: Record<string, string> = {
        'units': 'Unit', 'buildings': 'Building', 'tiles': 'Tile', 'resources': 'Resource',
    };

    // Reverse-index pack slots. Entity packs give their slot keys a section
    // and a label; metadata packs are single-slot icon indirections and only
    // contribute a usage to the target key.
    const slotOwner = new Map<string, {ownerId: string; level: number; variation: number}>();
    const slotUsages = new Map<string, ImageUsage[]>();
    const iconUsages = new Map<string, ImageUsage[]>();
    // An entity's "main" image is the top-left of its pack — level 0 for most,
    // but `startLevel` for buildings (column 0 is a hidden dead slot). That slot
    // IS the entity image (the standalone "entity image" concept is retired), so
    // it's labelled with just the entity name; other slots keep the level/# suffix.
    const buildingStart = new Map((state.buildings ?? []).map(b => [b.id, b.startLevel ?? 0]));
    // First *visible* row of an entity pack (its "Level 1"): startLevel for
    // buildings (rows below are hidden dead slots the runtime never shows),
    // clamped into range; row 0 for everything else.
    function displayBase(packId: string): number {
        const pack = state.imagePacks?.[packId];
        if (!pack || pack.length === 0) return 0;
        return buildingStart.has(packId)
            ? Math.max(0, Math.min(pack.length - 1, buildingStart.get(packId)!))
            : 0;
    }
    // 1-based level shown to designers (matches the pack popup's "Level N").
    function displayLevel(packId: string, level: number): number {
        return level - displayBase(packId) + 1;
    }
    for (const [packId, pack] of Object.entries(state.imagePacks ?? {})) {
        if (entitySection.has(packId)) {
            const db = displayBase(packId);
            for (let level = 0; level < pack.length; level++) {
                // Dead slots (below the first visible level) are never rendered:
                // not a usage and not an owner, so an image pinned only there
                // reads as an orphan vestige that can be cleaned up.
                if (level < db) continue;
                const varCount = pack[level]?.length ?? 0;
                for (let variation = 0; variation < varCount; variation++) {
                    const k = pack[level][variation];
                    if (!k) continue;
                    if (!slotOwner.has(k)) slotOwner.set(k, {ownerId: packId, level, variation});
                    const base = `${KIND_SINGULAR[entitySection.get(packId)!]}: ${getLocalizationValue(packId)}`;
                    const list = slotUsages.get(k) ?? [];
                    list.push({
                        kind: 'packSlot',
                        ref: packId,
                        label: `${base} V${variation + 1} L${displayLevel(packId, level)}`,
                    });
                    slotUsages.set(k, list);
                }
            }
        } else if (metadataUids.has(packId)) {
            const target = pack[0]?.[0];
            if (target && target !== packId) {
                const list = iconUsages.get(target) ?? [];
                list.push({
                    kind: 'metadataIcon',
                    ref: packId,
                    label: `${metadataCollectionOf.get(packId)} icon: ${getLocalizationValue(packId)}`,
                });
                iconUsages.set(target, list);
            }
        }
    }

    // Reverse-index building construction frames (the
    // constructionImagePacks dict: building id → ordered keys). Gives these keys
    // a building usage + section so they aren't mistaken for orphans.
    const constructionOwner = new Map<string, {ownerId: string; index: number}>();
    const constructionUsages = new Map<string, ImageUsage[]>();
    for (const [buildingId, frames] of Object.entries(state.constructionImagePacks ?? {})) {
        (frames ?? []).forEach((k, index) => {
            if (!k) return;
            if (!constructionOwner.has(k)) constructionOwner.set(k, {ownerId: buildingId, index});
            const list = constructionUsages.get(k) ?? [];
            list.push({
                kind: 'construction',
                ref: buildingId,
                label: `Building: ${getLocalizationValue(buildingId)} — Construction ${index + 1}`,
            });
            constructionUsages.set(k, list);
        });
    }

    // --- per-key classification ------------------------------------------
    const result = new Map<string, ClassifiedImage>();
    for (const key of Object.keys(state.images ?? {})) {
        let section: ImageSectionId;
        let label: string;

        const owner = slotOwner.get(key);
        const slotMatch = key.match(SLOT_KEY_RE);
        if (owner) {
            // Referenced by a visible pack slot — name it by entity, variant, level.
            section = entitySection.get(owner.ownerId)!;
            label = `${getLocalizationValue(owner.ownerId)} V${owner.variation + 1} L${displayLevel(owner.ownerId, owner.level)}`;
        } else if (entitySection.has(key)) {
            // Entity-id key not referenced by any visible slot — a vestige/orphan.
            section = entitySection.get(key)!;
            label = getLocalizationValue(key);
        } else if (slotMatch && entitySection.has(slotMatch[1])) {
            // Orphaned positional key (its pack no longer references it).
            section = entitySection.get(slotMatch[1])!;
            label = getLocalizationValue(slotMatch[1]);
        } else if (metadataUids.has(key)) {
            section = 'metadata-icons';
            label = getLocalizationValue(key);
        } else if (constructionOwner.has(key)) {
            // A building construction frame (not a pack slot). Section it under
            // its building with a "Construction N" label.
            const o = constructionOwner.get(key)!;
            section = 'buildings';
            label = `${getLocalizationValue(o.ownerId)} — Construction ${o.index + 1}`;
        } else if (key.startsWith('ui.') || codeKeys.has(key)) {
            section = 'ui';
            label = key;
        } else {
            section = 'unclassified';
            label = key;
        }

        // The "entity image" concept is retired: an entity is represented by its
        // pack's top-left slot (added via slotUsages below), not a privileged
        // images[<id>] entry. A bare entity-id key that the pack no longer
        // references therefore reads as an orphan — which is what surfaces it for
        // cleanup. The key still lands in its entity section via the branch above.
        const usedBy: ImageUsage[] = [];
        usedBy.push(...(slotUsages.get(key) ?? []));
        if (metadataUids.has(key))
            usedBy.push({
                kind: 'metadataIcon',
                ref: key,
                label: `${metadataCollectionOf.get(key)} icon: ${getLocalizationValue(key)}`,
            });
        usedBy.push(...(iconUsages.get(key) ?? []));
        usedBy.push(...(constructionUsages.get(key) ?? []));
        // Any section: a key the Client names is used even when data also claims it
        // (the Mana collectable icon, a construction fallback).
        const codeUsage = codeKeys.get(key);
        if (codeUsage)
            usedBy.push({kind: 'code', ref: key, label: codeUsage});
        else if (section === 'ui')
            usedBy.push({kind: 'code', ref: key, label: 'Client code — key is referenced from C#'});

        const pack = owner
            ? {
                owner: owner.ownerId,
                level: owner.level,
                variation: owner.variation,
                main: owner.level === displayBase(owner.ownerId) && owner.variation === 0,
            }
            : undefined;
        const co = constructionOwner.get(key);
        result.set(key, {key, section, label, usedBy, pack,
            constructionOf: co ? {owner: co.ownerId, index: co.index} : undefined});
    }

    return result;
}
